import React, { useState } from 'react';

/**
 * DiscoverElementsPanel (v2)
 * 
 * Element-driven discovery system:
 * 1. Element chips (e.g., "Tall Tower", "Sparkly Eyes")
 * 2. Click element → unlocks → reveals concept chips
 * 3. Click concept → shows detailed coaching block
 * 4. Progress = unlocked elements / total elements
 */
const DiscoverElementsPanel = ({ 
  analysis, // v2 agentic schema format
  artworkImageUrl,
  onElementClick,
  onConceptClick,
  activeElementIndex = null, // Sync with image overlay
  onElementIndexChange = null // Callback when element should be unlocked from image click
}) => {
  const [unlockedElements, setUnlockedElements] = useState(new Set());
  const [selectedElementId, setSelectedElementId] = useState(null);
  const [selectedConceptId, setSelectedConceptId] = useState(null);
  
  // Sync with image overlay: if activeElementIndex changes, unlock that element
  React.useEffect(() => {
    if (activeElementIndex != null && analysis?.discover?.elements && Array.isArray(analysis.discover.elements)) {
      const element = analysis.discover.elements[activeElementIndex];
      if (element && element.id && element.label) {
        // Always unlock and select when hotspot is clicked
        setUnlockedElements((prev) => {
          if (!prev.has(element.id)) {
            const newUnlocked = new Set(prev);
            newUnlocked.add(element.id);
            console.log('[DiscoverElementsPanel] Auto-unlocked element:', element.id, 'at index:', activeElementIndex);
            return newUnlocked;
          }
          return prev;
        });
        // Always select the element when hotspot is clicked
        setSelectedElementId(element.id);
        setSelectedConceptId(null);
        onElementClick?.(element.id);
      }
    }
  }, [activeElementIndex, analysis?.discover?.elements, onElementClick]);

  // Validate v2 structure exists and has elements
  if (!analysis || !analysis.discover) {
    return (
      <div className="rounded-2xl border-2 border-amber-200 shadow-lg p-4 md:p-5 bg-gradient-to-br from-amber-50 via-amber-100/70 to-yellow-100">
        <p className="text-amber-800 text-sm">Loading discoveries...</p>
      </div>
    );
  }

  const { discover, meta } = analysis;
  const { elements = [], roleTitle } = discover;
  
  // Validate elements array exists and has items
  if (!Array.isArray(elements) || elements.length === 0) {
    return (
      <div className="rounded-2xl border-2 border-amber-200 shadow-lg p-4 md:p-5 bg-gradient-to-br from-amber-50 via-amber-100/70 to-yellow-100">
        <p className="text-amber-800 text-sm">No discovery elements available for this artwork.</p>
      </div>
    );
  }

  // Filter out invalid elements (must have id, label, concepts)
  const validElements = elements.filter(el => 
    el && 
    el.id && 
    el.label && 
    Array.isArray(el.concepts) && 
    el.concepts.length > 0
  );

  if (validElements.length === 0) {
    return (
      <div className="rounded-2xl border-2 border-amber-200 shadow-lg p-4 md:p-5 bg-gradient-to-br from-amber-50 via-amber-100/70 to-yellow-100">
        <p className="text-amber-800 text-sm">Discovery elements are incomplete. Please try analyzing again.</p>
      </div>
    );
  }

  const totalElements = validElements.length;
  const unlockedCount = unlockedElements.size;

  const handleElementClick = (elementId) => {
    const newUnlocked = new Set(unlockedElements);
    newUnlocked.add(elementId);
    setUnlockedElements(newUnlocked);
    setSelectedElementId(elementId);
    setSelectedConceptId(null); // Reset concept selection when element changes
    onElementClick?.(elementId);
  };

  const handleConceptClick = (conceptId) => {
    setSelectedConceptId(conceptId);
    onConceptClick?.(conceptId);
  };

  const selectedElement = validElements.find(el => el.id === selectedElementId);
  const selectedConcept = selectedElement?.concepts?.find(c => c.id === selectedConceptId);

  // Helper: extract an "objects" sentence from the first concept's "what" text.
  // We rely on the specialist prompt which forces the FIRST sentence of "what"
  // to name 2–4 concrete objects inside the element area.
  const getObjectsSentence = (element) => {
    if (!element || !Array.isArray(element.concepts) || element.concepts.length === 0) return null;
    const firstConcept = element.concepts[0];
    const whatText = firstConcept?.details?.what;
    if (!whatText || typeof whatText !== 'string') return null;
    const firstSentence = whatText.split(/[.?!]/)[0].trim();
    if (!firstSentence) return null;
    return firstSentence;
  };

  return (
    <div className="rounded-2xl border-2 border-amber-200 shadow-lg p-4 md:p-5 bg-gradient-to-br from-amber-50 via-amber-100/70 to-yellow-100 flex flex-col h-full min-h-0">
      {/* Role Title */}
      <h3 className="text-lg md:text-xl font-bold text-amber-900 mb-2">
        {roleTitle || 'Discover This Artwork'}
      </h3>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs md:text-sm font-semibold text-amber-800">
            Unlocked {unlockedCount}/{totalElements} Elements
          </span>
        </div>
        <div className="w-full bg-amber-200 rounded-full h-2.5">
          <div 
            className="bg-amber-500 h-2.5 rounded-full transition-all duration-300"
            style={{ width: totalElements > 0 ? `${(unlockedCount / totalElements) * 100}%` : '0%' }}
          />
        </div>
      </div>

      {/* Element Chips - Horizontal Scroll */}
      <div className="mb-4 overflow-x-auto pb-2">
        <div className="flex gap-2 min-w-max">
          {validElements.map((element) => {
            const isUnlocked = unlockedElements.has(element.id);
            const isSelected = selectedElementId === element.id;
            
            return (
              <button
                key={element.id}
                type="button"
                onClick={() => handleElementClick(element.id)}
                className={`
                  px-3 py-2 rounded-xl text-xs md:text-sm font-semibold border transition-all whitespace-nowrap
                  ${isUnlocked 
                    ? 'bg-amber-500 text-white border-amber-600 shadow-md' 
                    : 'bg-white/70 text-amber-800 border-amber-300 hover:bg-amber-100'
                  }
                  ${isSelected ? 'ring-2 ring-amber-400 ring-offset-1' : ''}
                `}
              >
                {isUnlocked ? '✓ ' : '🔓 '}
                {element.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected Element Section - Scrollable */}
      {selectedElement && (
        <div className="mb-4 space-y-3 flex-shrink-0">
          {/* Element Header */}
          <div className="rounded-xl border border-amber-300 bg-white/80 p-3">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h4 className="text-base font-bold text-amber-900 mb-1">
                  {selectedElement.label}
                </h4>
                <p className="text-xs text-amber-700">
                  Look: {selectedElement.whereToLook}
                </p>
              </div>
              <span className="text-xs font-semibold text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                {selectedElement.unlockReward}
              </span>
            </div>

            {/* Art Concepts text - show a single, clear appreciation sentence instead of a chip */}
            {unlockedElements.has(selectedElement.id) && selectedElement.concepts && selectedElement.concepts.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-semibold text-amber-800 mb-2">
                  Art Concepts:
                </p>
                <p className="text-sm text-amber-900 leading-relaxed">
                  {getObjectsSentence(selectedElement) || selectedElement.concepts[0]?.details?.what}
                </p>
              </div>
            )}
          </div>

          {/* Micro Challenge - Show when element is unlocked */}
          {unlockedElements.has(selectedElement.id) && selectedElement.microChallenge && (
            <div className="p-3 bg-amber-100 rounded-lg border border-amber-200">
              <p className="text-xs font-semibold text-amber-800 mb-1">Challenge:</p>
              <p className="text-xs text-amber-900">{selectedElement.microChallenge}</p>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!selectedElement && unlockedCount === 0 && (
        <div className="text-center py-8">
          <p className="text-amber-800 font-semibold text-sm mb-2">
            Tap an element chip above to start discovering!
          </p>
          <p className="text-amber-700 text-xs">
            Each element hides artist tricks you can learn.
          </p>
        </div>
      )}
    </div>
  );
};

export default DiscoverElementsPanel;
